import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowSquareOut, DeviceMobile, Monitor, X } from '@phosphor-icons/react';
import { LivePreview, type PreviewDraft } from './LivePreview';
import { cn } from '@/lib/utils/cn';
import type { PortfolioSectionKind, TemplateId } from '@/types/portfolio';
import { TEMPLATE_CATALOG } from '@/features/templates/catalog';

type DeviceKind = 'desktop' | 'mobile';

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  draft: PreviewDraft;
  enabled: ReadonlySet<PortfolioSectionKind>;
  ownerName: string;
  publicUrl?: string;
  template?: TemplateId;
}

export function PreviewDrawer({ open, onOpenChange, draft, enabled, ownerName, publicUrl, template }: Props) {
  const [device, setDevice] = useState<DeviceKind>('desktop');
  const [activeTemplate, setActiveTemplate] = useState<TemplateId>(template ?? 'minimal');

  useEffect(() => {
    if (template) setActiveTemplate(template);
  }, [template]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in data-[state=closed]:fade-out" />
        <Dialog.Content
          className="fixed right-0 top-0 z-50 flex h-dvh w-full max-w-[1100px] flex-col bg-board shadow-float data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right"
        >
          <div className="flex items-center justify-between border-b border-line px-6 py-4">
            <div className="flex items-center gap-4">
              <Dialog.Title className="font-display text-base font-semibold tracking-tight">
                Live preview
              </Dialog.Title>
              <div className="inline-flex overflow-hidden rounded-md border border-line text-ink-muted">
                <button
                  type="button"
                  onClick={() => setDevice('desktop')}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
                    device === 'desktop' ? 'bg-rail text-ink' : 'hover:bg-rail/60',
                  )}
                >
                  <Monitor size={14} /> Desktop
                </button>
                <button
                  type="button"
                  onClick={() => setDevice('mobile')}
                  className={cn(
                    'flex items-center gap-1.5 border-l border-line px-3 py-1.5 text-xs font-medium transition-colors',
                    device === 'mobile' ? 'bg-rail text-ink' : 'hover:bg-rail/60',
                  )}
                >
                  <DeviceMobile size={14} /> Mobile
                </button>
              </div>
              <label className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-ink-faint">
                Template
                <select
                  value={activeTemplate}
                  onChange={(e) => setActiveTemplate(e.target.value as TemplateId)}
                  className="rounded-md border border-line bg-board px-2 py-1 font-mono text-[11px] text-ink focus:border-ink focus:outline-none"
                >
                  {TEMPLATE_CATALOG.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex items-center gap-2">
              {publicUrl ? (
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-1.5 text-xs font-medium text-ink hover:bg-rail"
                >
                  Open live <ArrowSquareOut size={12} />
                </a>
              ) : null}
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="rounded-md border border-line bg-board p-2 text-ink-muted hover:text-ink"
                  aria-label="Close preview"
                >
                  <X size={14} />
                </button>
              </Dialog.Close>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto bg-canvas">
            <div className="mx-auto py-10 px-6">
              <div
                className={cn(
                  'mx-auto overflow-hidden rounded-xl border border-line bg-board shadow-sm transition-all',
                  device === 'desktop' ? 'w-full max-w-4xl' : 'w-full max-w-[390px]',
                )}
              >
                <LivePreview draft={draft} enabled={enabled} ownerName={ownerName} template={activeTemplate} />
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
